import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import type { Deposito } from '@abastece/core';
import {
  DEFAULT_SETTINGS,
  centavos,
  formatMoney,
  fxRate,
  hoy,
  newId,
  parseMoney,
  usdToCordobas,
  type Centavos,
  type CuentaBancoCordobas,
  type CuentaBancoDolares,
} from '@abastece/shared';
import { readSettings } from '../db/reader.js';
import { conCuentasPuestas, cuentaEnCordobas, cuentasAbiertas, nombreDeCuenta, primeraEnDolares } from '../lib/bancos.js';
import { emitirYVer } from '../lib/emitir.js';
import { useT } from '../i18n/index.js';
import type { Paso } from './Asistente.js';
import { Campo } from './Campo.js';
import { Dato, DatoTexto, enDolares, fechaCorta } from './CuentasDeCaja.js';
import { ElegirCuenta } from './ElegirCuenta.js';

/**
 * Cash taken to the bank, a question to a screen: how much of each currency, the slip's number and
 * its day, and a check. Córdobas go to a córdoba account and dollars to a dollar account, which one
 * asked only where the shop has more than one (`ElegirCuenta`), both out of general cash
 * (`efectivo.depositado`), on the day of the slip: a week is gone over at its end, and the bank's
 * statement has the deposit on the day it was made.
 */

export const PASOS_DE_DEPOSITO = ['deposito-monto', 'deposito-boleta', 'deposito-revise'] as const;

/** An amount as typed: nothing typed is none, anything else must be an amount. */
const leer = (texto: string): Centavos | null => (texto.trim() === '' ? centavos(0) : parseMoney(texto));

export function usePasosDeDeposito({ ir, alGuardar }: { ir: (id: string) => void; alGuardar: (d: Deposito) => void }): {
  pasos: Paso[];
  empezar: () => void;
} {
  const t = useT();
  const config = useLiveQuery(() => readSettings(), [], DEFAULT_SETTINGS);
  const [cordobas, setCordobas] = useState('');
  const [dolares, setDolares] = useState('');
  const [boleta, setBoleta] = useState('');
  const [elegidaC, setElegidaC] = useState<CuentaBancoCordobas | null>(null);
  const [elegidaD, setElegidaD] = useState<CuentaBancoDolares | null>(null);
  const [dia, setDia] = useState('');
  const fecha = dia || hoy();
  const fechaValida = fecha <= hoy();
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const c = leer(cordobas);
  const d = leer(dolares);
  // The account picked while it is still open, or the one it starts on.
  const cuentaC = cuentaEnCordobas(config, elegidaC);
  const cuentaD = cuentasAbiertas(config, 'USD').some((x) => x.cuenta === elegidaD) ? elegidaD! : primeraEnDolares(config);
  /*
   * Not held against general cash on the books: they start at nothing and the first float comes
   * out of them, so in most shops they are below what the safe holds until it is counted, and a
   * warning there would come up on every deposit and teach everybody to pass it by.
   */
  const valido = c !== null && c >= 0 && d !== null && d >= 0 && c + d > 0 && fechaValida;

  async function guardar() {
    if (!valido) return;
    setGuardando(true);
    setError(null);
    const depositoId = newId();
    try {
      const { rechazo } = await emitirYVer('efectivo.depositado', {
        depositoId,
        fecha,
        cordobas: c!,
        dolares: d!,
        ...(c! > 0 ? { cuenta: cuentaC } : {}),
        ...(d! > 0 ? { cuentaDolares: cuentaD } : {}),
        ...(boleta.trim() ? { referencia: boleta.trim() } : {}),
      });
      if (rechazo) {
        setError(rechazo);
        return;
      }
      alGuardar({
        id: depositoId,
        fecha,
        cordobas: c!,
        dolares: d!,
        valorDolares: usdToCordobas(d!, fxRate(config.fxTasaOficial)),
        ...(c! > 0 ? { cuenta: cuentaC } : {}),
        ...(d! > 0 ? { cuentaDolares: cuentaD } : {}),
        referencia: boleta.trim() || null,
        nota: null,
        rev: 0,
      });
      setCordobas('');
      setDolares('');
      setBoleta('');
      setDia('');
      setElegidaC(null);
      setElegidaD(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('entrega.noSeGuardo'));
    } finally {
      setGuardando(false);
    }
  }

  const pasos: Paso[] = [
    {
      id: 'deposito-monto',
      titulo: t('deposito.monto'),
      explicacion: <p>{t('deposito.montoExplicacion')}</p>,
      contenido: (
        <div className="flex flex-col gap-4">
          <Campo
            etiqueta={t('deposito.cordobas')}
            inputMode="decimal"
            className="tabular text-lg"
            value={cordobas}
            onChange={(e) => setCordobas(e.target.value)}
            placeholder="0.00"
            autoComplete="off"
            error={c === null || (c !== null && c < 0) ? t('caja.montoInvalido') : null}
            {...(cuentasAbiertas(config, 'NIO').length > 1 ? {} : { ayuda: t('deposito.cordobasAyuda') })}
          />
          <ElegirCuenta valor={cuentaC} onCambiar={(x) => setElegidaC(x as CuentaBancoCordobas)} etiqueta={t('mover.cordobasA')} />
          <Campo
            etiqueta={t('deposito.dolares')}
            inputMode="decimal"
            className="tabular text-lg"
            value={dolares}
            onChange={(e) => setDolares(e.target.value)}
            placeholder="0.00"
            autoComplete="off"
            error={d === null || (d !== null && d < 0) ? t('caja.montoInvalido') : null}
            {...(cuentasAbiertas(config, 'USD').length > 1 ? {} : { ayuda: t('deposito.dolaresAyuda') })}
          />
          <ElegirCuenta soloDolares valor={cuentaD} onCambiar={(x) => setElegidaD(x as CuentaBancoDolares)} etiqueta={t('mover.dolaresA')} />
        </div>
      ),
      seguir: { etiqueta: t('asistente.siguiente'), deshabilitado: !valido, alTocar: () => ir('deposito-boleta') },
    },
    {
      id: 'deposito-boleta',
      titulo: t('deposito.boleta'),
      explicacion: <p>{t('deposito.boletaExplicacion')}</p>,
      contenido: (
        <div className="flex flex-col gap-4">
          <Campo
            maxLength={80}
            etiqueta={t('deposito.numeroBoleta')}
            value={boleta}
            onChange={(e) => setBoleta(e.target.value)}
            autoComplete="off"
          />
          <Campo
            etiqueta={t('deposito.fecha')}
            type="date"
            value={fecha}
            max={hoy()}
            onChange={(e) => setDia(e.target.value)}
            error={fechaValida ? null : t('gastoPaso.fechaFutura')}
          />
        </div>
      ),
      seguir: { etiqueta: t('asistente.siguiente'), deshabilitado: !fechaValida, alTocar: () => ir('deposito-revise') },
    },
    {
      id: 'deposito-revise',
      titulo: t('turno.revise'),
      explicacion: <p>{t('deposito.reviseExplicacion')}</p>,
      contenido: (
        <div className="flex flex-col gap-4">
          <div className="tarjeta flex flex-col gap-2">
            <Dato etiqueta={t('deposito.alBancoCordobas')} valor={formatMoney(c ?? centavos(0))} />
            {conCuentasPuestas(config) && (c ?? 0) > 0 && <DatoTexto etiqueta={t('mover.cordobasA')} valor={nombreDeCuenta(config, cuentaC, t)} />}
            <Dato etiqueta={t('deposito.alBancoDolares')} valor={enDolares(d ?? centavos(0))} />
            {conCuentasPuestas(config) && (d ?? 0) > 0 && <DatoTexto etiqueta={t('mover.dolaresA')} valor={nombreDeCuenta(config, cuentaD, t)} />}
            {boleta.trim() && <Dato etiqueta={t('deposito.numeroBoleta')} valor={boleta.trim()} />}
            <Dato etiqueta={t('cuadrar.fecha')} valor={fechaCorta(fecha)} />
          </div>
          {error && <p className="text-sm text-rose-400">{error}</p>}
        </div>
      ),
      seguir: {
        etiqueta: t('deposito.guardar'),
        icono: 'guardar',
        deshabilitado: guardando || !valido,
        alTocar: () => void guardar(),
      },
    },
  ];

  return {
    pasos,
    empezar: () => {
      setError(null);
      ir('deposito-monto');
    },
  };
}
